import React from 'react'
import { IconButton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { formatAsCurrency } from 'services/formatting'
import Tag from 'components/Tag'
import { Create } from '@mui/icons-material'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { useFragment, graphql } from 'react-relay/hooks'
import { CompensationSummary_User$key } from '__generated__/CompensationSummary_User.graphql'
import useStyles from './styles'

export interface ICompensationSummaryProps {
  editable?: boolean
  user: CompensationSummary_User$key
  onEditOpen: () => void
}

function CompensationSummary(props: ICompensationSummaryProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef, onEditOpen } = props

  const user = useFragment(graphql`
    fragment CompensationSummary_User on User {
      self
      profile {
        currency { code }
        freelancerRate {
          value
        }
        clientRate {
          value
        }
        jobTypes
        rateMode
      }
    }
  `, userFragmentRef)

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  const useFreelancerRate = user?.self || editable
  const rate = useFreelancerRate ? user?.profile?.freelancerRate?.value : user?.profile?.clientRate?.value
  const currency = user?.profile?.currency?.code
  const formattedRate = rate && currency ? formatAsCurrency(rate, { currency }) : '?'
  const rateMode = user?.profile?.rateMode || 'hour'

  const isFreelance = Boolean((user?.profile?.jobTypes || []).includes('freelance') && (rate || editable))
  const isPermanent = Boolean((user?.profile?.jobTypes || []).includes('permanent') && (rate || editable))

  const error = editable && (!user?.profile?.jobTypes?.length || !user?.profile?.freelancerRate?.value) ? 'Compensation expectations are required' : null

  const editButton = editable && (
    <IconButton
      onClick={onEditOpen}
      data-cy="profile-rate-edit"
      className={classes.edit}
      size="large"
    >
      <Create />
    </IconButton>
  )

  const errorSummary = error && (
    <div className={classes.errorInline}>
      <Typography variant="body1" component="span">
        {error}
      </Typography>
    </div>
  )

  if (isFreelance && isPermanent) {
    return (
      <Tag className={classes.tag} data-cy="freelancer-rate">
        <Typography variant="body1" component="span">
          Freelance or Permanent from <strong>{formattedRate}/{rateMode}</strong> {editButton}
        </Typography>
        {errorSummary}
      </Tag>
    )
  }

  if (isFreelance || isPermanent) {
    return (
      <Tag className={classes.tag} data-cy="freelancer-rate">
        <Typography variant="body1" component="span">
          {isFreelance ? 'Freelance' : 'Permanent'} from <strong>{formattedRate}/{rateMode}</strong> {editButton}
        </Typography>
        {errorSummary}
      </Tag>
    )
  }

  if (!editable) {
    return null
  }

  return (
    <Tag className={classes.tag}>
      <Typography variant="body1" component="span">
        {isMobile ? (
          'Job Type & Compensation'
        ) : (
          'Enter your job type and compensation expectations'
        )}

        {editButton}
      </Typography>
      {errorSummary}
    </Tag>
  )
}

export default React.memo(CompensationSummary)
